import { v4 as uuidv4 } from 'uuid'
import countries from 'i18n-iso-countries'
import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import FormValidationMixin from '@/components/mixins/form-validation-mixin'
import DatatransLightboxMixin from '@/components/mixins/datatrans-lightbox-mixin'
import _ from 'lodash'

import * as enLocale from 'i18n-iso-countries/langs/en.json'
import * as frLocale from 'i18n-iso-countries/langs/fr.json'

countries.registerLocale(enLocale)
countries.registerLocale(frLocale)

export default {
  name: 'userAddress',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError'
  ],
  props: {
    userInfoProp: {
      default () { return null }
    },
    accessorComponentProp: {
      type: String,
      default () { return 'purchasePlan' }
    }
  },
  watch: {
    userInfoProp (newVal) {
      this.userInfo = this.$utils.clone(newVal)
      this.setUserAddressData()
    },
    addressForm: {
      handler (newVal, prevVal) {
        this.isAddressFormDataChanged = _.isEqual(newVal, prevVal)
      },
      deep: true
    }
  },
  mixins: [AuthMixin, FormValidationMixin, DatatransLightboxMixin],
  data () {
    return {
      accessorComponentName: { purchasePlan: 'purchasePlan', userProfile: 'userProfile' },
      accessorComponent: this.accessorComponentProp,
      submitButtonLabel: (this.accessorComponentProp === 'purchasePlan') ? this.$t('label.next') : this.$t('label.saveChanges'),
      userInfo: this.$utils.clone(this.userInfoProp),
      addressForm: {},
      error: '',
      cityOptions: [],
      stateOptions: [],
      dropdownCityLoader: false,
      googleMapSessionToken: null,
      panelHeaderIconClass: { icon: true },
      panelHeaderTitle: this.$t('label.billingDetails'),
      showStateDropdown: false,
      addressFormRules: {
        firstName: [
          { required: true, message: this.$t('label.requiredFirstName') }
        ],
        lastName: [
          { required: true, message: this.$t('label.requiredLastName') }
        ],
        companyName: [
          { required: true, message: this.$t('label.requiredCompanyName') }
        ],
        streetNumber: [
          { required: true, message: this.$t('label.requiredStreetAndNumber') }
        ],
        city: [
          { required: true, message: this.$t('label.requiredCity') }
        ],
        zip: [
          { required: true, message: this.$t('label.requiredZipOrPostalCode') }
        ]
      },
      USCountryCode: ['US'],
      currentRequestsFlag: [],
      countries: [],
      creditCardInfo: {
        ccType: null,
        ccNumber: null,
        ccExpiry: null
      },
      usStates: [],
      isAddressForCustomerInfo: false,
      isAddressForCustomerBilling: false,
      isAddressFormDataChanged: false
    }
  },
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('user-address-change', { action, payload: { data } })
      }
    },
    setUserAddressData () {
      this.addressForm = this.filterAddressFormValue(this.userInfo.address)
      this.handleStateDropdownVisibility()
      this.setCreditCardInfo()
      this.setAddressUsage()
    },
    setCreditCardInfo () {
      const creditCardInfo = {
        ccType: null,
        ccNumber: null,
        ccExpiry: null
      }

      if (this.userInfo && this.userInfo.ccInfo) {
        creditCardInfo.ccType = this.userInfo.ccInfo.type
        creditCardInfo.ccNumber = this.userInfo.ccInfo.number
        creditCardInfo.ccExpiry = `${this.userInfo.ccInfo.expm}/${this.userInfo.ccInfo.expy}`
      }

      this.creditCardInfo = creditCardInfo
    },
    setAddressUsage () {
      this.isAddressForCustomerInfo = true
      this.isAddressForCustomerBilling = false
      this.panelHeaderTitle = this.$t('label.customerInfo')

      // Should not be visible in enterprise
      const hasUserAdminOrBillingRights = (this.$currentUser.hasGroupUserAdmin() || this.$currentUser.hasGroupUserBilling()) && !this.$currentUser.hasGroupEnterprise()
      const isFreeUser = (this.$currentUser.isStandAloneUser() || this.$currentUser.isFreeUser()) && this.accessorComponent === 'purchasePlan'

      if (hasUserAdminOrBillingRights || isFreeUser) {
        this.isAddressForCustomerInfo = false
        this.isAddressForCustomerBilling = true
        this.panelHeaderTitle = this.$t('label.billingDetails')
      }
    },
    setCountries () {
      const selectedLanguage = (this.$dataStore.getLanguage() && this.$dataStore.getLanguage().code === 'fr') ? 'fr' : 'en'
      this.countries = countries.getNames(selectedLanguage, { select: 'official' }) // All names by their ISO 3166-1 Alpha-2
    },
    filterAddressFormValue (data) {
      const address = this.$utils.clone(data)

      if (address.country) {
        if (address.country.length > 2) address.countryCode = countries.getAlpha2Code(address.country, 'en') // If country name is present, then get the ISO 3166-1 Alpha-2 code
        delete address.country
      }

      return address
    },
    handleCCIconClick () {
      if (this.isAddressFormDataChanged) {
        this.handleAddressFormSubmit({
          autoSave: true,
          nextAction: () => {
            this.datatransLightboxCreateAnAlias()
          }
        })
      } else {
        this.datatransLightboxCreateAnAlias()
      }
    },
    handleAddressFormSubmit (params = {}) {
      const sendDataToBackend = () => {
        this.$utils.mainLoader(true)

        const addressForm = this.$utils.clone(this.addressForm)

        if (_.isPlainObject(addressForm.city)) {
          addressForm.city = addressForm.city.name
        }

        const currentCountryCode = this.addressForm.countryCode ?? null
        if ((this.USCountryCode.indexOf(currentCountryCode) < 0) && addressForm.state) {
          delete addressForm.state
        }

        delete addressForm.country

        const requestData = {
          billing_address: addressForm
        }

        this.$api.put(`${CONFIG.API_BASE_URL}/group/info`, requestData)
          .then((response) => {
            if (params.nextAction) {
              params.nextAction()
              this.isAddressFormDataChanged = false
              this.$utils.mainLoader(false)
            } else {
              this.getUserUserInfoFromBackend({
                callback: () => {
                  // Send an event that users info was updated to infrom parent component and update usersIfo data
                  this.emitEvent('USERS_INFO_ADDRESS_UPDATED', requestData.billing_address)

                  this.$utils.mainLoader(false)
                  if (this.accessorComponent === 'purchasePlan') {
                    this.$router.push(`/purchase/${this.$route.params.plan}/summary`, () => {})
                  } else {
                    EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
                      description: this.$t('label.updatedCustomerInfo'),
                      callbacks: {
                        eventOnClose: () => {},
                      }
                    })
                  }
                }
              })
            }
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { sendDataToBackend() } })
          })
      }

      if (params.autoSave) {
        sendDataToBackend()
      } else {
        const formEl = $(this.$refs.addressForm.$el)
        formEl.find('.el-form-item').removeClass('animated shake')

        this.$refs.addressForm.validate((isFormValid) => {
          if (isFormValid) {
            // Validate state if US Country
            formEl.find('.el-form-item.state').removeClass('animated shake is-error')
            const currentCountryCode = (this.addressForm.countryCode) ? this.addressForm.countryCode : null
            if ((this.USCountryCode.indexOf(currentCountryCode) >= 0) && (!this.addressForm.state || (this.addressForm.state && this.addressForm.state === ''))) {
              this.$refs.addressForm.validateField('countryState')
              $('.state').removeClass('is-success').addClass('is-error')
              window.setTimeout(() => { this.$utils.animate($('.state'), 'animated shake') }, 1)

              return false
            }

            sendDataToBackend()
          } else {
            // Animate error fields
            formEl.find('.el-form-item').each((indx, el) => {
              if (($(el).hasClass('is-error'))) {
                window.setTimeout(() => { this.$utils.animate(el, 'animated shake') }, 1)
              }
            })

            return false
          }
        })
      }
    },
    handleCityDropdownEvent (evtType, value) {
      if (evtType === 'changed') {
        this.handleCityDropdownChange(value)
        this.handleCitySearchPopperVisibility({ style: { display: 'none' } })
      }

      const cityElFormItem = $('.el-form-item.city')

      if (evtType === 'focus') {
        cityElFormItem.find('.el-form-item__error').addClass('transparent')
        cityElFormItem.find('input').addClass('transparent-placeholder')
      }

      if (evtType === 'blur' && !this.addressForm.city) {
        cityElFormItem.find('.el-form-item__error').removeClass('transparent')
        cityElFormItem.find('input').removeClass('transparent-placeholder')
      }
    },
    handleCityDropdownTyping ($event) {
      const inputEl = $('#addressCustomCityAutoSearch')
      const inputWrapperEl = inputEl.parent()
      const isFocused = inputWrapperEl.hasClass('is-focus')

      if (!isFocused) {
        const cityName = $($event.target).val()
        const currentSelectBoxOffset = inputEl.parent().offset()
        const leftStyleVal = currentSelectBoxOffset.left
        let topStyleVal = currentSelectBoxOffset.top

        if (this.$utils.is1920View()) {
          topStyleVal += 40
        } else {
          topStyleVal += 25
        }

        this.handleCitySearchPopperVisibility({ style: { display: 'block', top: topStyleVal, left: leftStyleVal } })
        this.debouncedSearch(cityName)
      }
    },
    handleCitySearchPopperVisibility (params) {
      if (!params) params = { style: {} }
      if ($('.address-city-autosearch-proper').length) {
        $('.address-city-autosearch-proper').css(params.style)
      }
    },
    /**
      * Search city name
      * @param {String} - cityName
    */
    handleCityDropdownSearch (cityName) {
      const apiRequest = () => {
        this.dropdownCityLoader = true

        // Generate new session token request
        if (this.googleMapSessionToken === null) {
          this.googleMapSessionToken = uuidv4()
        }

        const lang = this.$t?.locale?.value ?? 'en'
        const placeAutoCompleteUrl = `${CONFIG.API_FEEDS_URL}/weather/place/autocomplete/json?input=${cityName}&type=(cities)&language=${lang}&sessiontoken=${this.googleMapSessionToken}`

        this.$api.get(placeAutoCompleteUrl)
          .then(response => {
            const searchResults = []
            const acceptedTypes = ['locality', 'sublocality', 'colloquial_area', 'administrative_area_level_1', 'administrative_area_level_2', 'administrative_area_level_3', 'administrative_area_level_4']

            for (const item of response.data.predictions) {
              if (item.types) {
                for (const indx in item.types) {
                  if (acceptedTypes.indexOf(item.types[indx]) >= 0) {
                    item.placeId = item.place_id
                    item.name = item.name ?? item.cityName ?? item.description
                    item.location = item.location ?? false
                    item.fullname = item.fullname ?? null

                    searchResults.push(item)
                    break
                  }
                }
              }
            }

            this.cityOptions = searchResults
            this.dropdownCityLoader = false
          })
          .catch((error) => {
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
          })
      }
      apiRequest()
    },
    /**
      * Get place details using placeId from current dropdown value
      * @param {Object} dropdownVal - dropdown value
    */
    handleCityDropdownChange (dropdownVal) {
      if (this.googleMapSessionToken !== null && dropdownVal.placeId && !dropdownVal.location) {
        const apiRequest = () => {
          const lang = this.$t?.locale?.value ?? 'en'
          const reqFields = 'fields=address_component,adr_address,alt_id,formatted_address,geometry,name,type,place_id'
          const placeCityDetailsUrl = `${CONFIG.API_FEEDS_URL}/weather/place/details/json?placeid=${dropdownVal.placeId}&type=geocode&language=${lang}&sessiontoken=${this.googleMapSessionToken}&${reqFields}`

          this.handleCurrentRequestsFlag('add', 'getPlaceDetails')

          this.$api.get(placeCityDetailsUrl)
            .then(response => {
              const responseData = {
                addressComponents: (response.data.result.address_components) ? response.data.result.address_components : null,
                geometry: (response.data.result.geometry) ? response.data.result.geometry : null,
                formattedAddress: (response.data.result.formatted_address) ? response.data.result.formatted_address : null,
                id: (response.data.result.id) ? response.data.result.id : null
              }

              // Get name and country
              // Prio -> sublocality -> locality -> country
              const acceptedTypeForCountry = ['country']
              let name = null
              let country = null
              let countryCode = null
              let state = null

              for (const addressComponent of responseData.addressComponents) {
                if (addressComponent.types) {
                  for (const indx in addressComponent.types) {
                    const addressCompType = addressComponent.types[indx]

                    if (name === null && addressCompType === 'sublocality') {
                      name = addressComponent.short_name
                    }

                    if (name === null && addressCompType === 'locality') {
                      name = addressComponent.short_name
                    }

                    if (name === null && addressCompType === 'postal_town') {
                      name = addressComponent.short_name
                    }

                    if (name === null && addressCompType === 'country') {
                      name = addressComponent.short_name
                    }

                    if (acceptedTypeForCountry.indexOf(addressCompType) >= 0) {
                      country = addressComponent.long_name
                      countryCode = addressComponent.short_name
                    }

                    if (addressCompType === 'administrative_area_level_1') {
                      state = addressComponent.short_name
                    }
                  }
                }
              }

              // Collate place details data
              const placeDetails = {}
              placeDetails.name = name
              placeDetails.fullname = `${name}, ${country}`
              placeDetails.country = country
              placeDetails.countryCode = countryCode
              placeDetails.description = responseData.formattedAddress
              placeDetails.state = state

              // Create new City Options and Reset google request session token
              const newCityOptions = []
              newCityOptions.push({
                name: placeDetails.name,
                fullname: placeDetails.fullname,
                location: OpenLocationCode.encode(responseData.geometry.location.lat, responseData.geometry.location.lng),
                placeId: dropdownVal.placeId,
                description: placeDetails.name
              })
              this.cityOptions = newCityOptions
              this.googleMapSessionToken = null

              // Hack to show the updated description of the city in the dropdown
              setTimeout(() => {
                $('.address-city-autosearch-proper').find('.el-select-dropdown__item.selected').click()
                this.$refs.addressCustomCityAutoSearch.blur()
              }, 1)

              // Set Country code. ISO 3166-1 Alpha-2 code
              const addressForm = this.$utils.clone(this.addressForm)
              addressForm.countryCode = countries.getAlpha2Code(placeDetails.country, 'en')

              if (this.USCountryCode.indexOf(addressForm.countryCode) >= 0 && placeDetails.state !== null) {
                addressForm.state = placeDetails.state
              }

              this.addressForm = this.filterAddressFormValue(addressForm)

              this.handleStateDropdownVisibility()
              this.handleCurrentRequestsFlag('remove', 'getPlaceDetails')
            })
            .catch((error) => {
              this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
              this.handleCurrentRequestsFlag('remove', 'getPlaceDetails')
            })
        }

        apiRequest()
      }
    },
    setUSStates () {
      const apiRequest = () => {
        // Get Search City from Google API
        this.$api.get(`${window.location.origin}/static/states/us_states.json`)
          .then(response => {
            this.usStates = response.data
          })
          .catch((error) => {
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
          })
      }

      apiRequest()
    },
    handleStateDropdownVisibility () {
      let showDropdown = false
      const currentCountryCode = (this.addressForm.countryCode) ? this.addressForm.countryCode : null

      if (this.USCountryCode.indexOf(currentCountryCode) >= 0) {
        showDropdown = true
      }

      // Reset state data
      if (!showDropdown) {
        const addressForm = this.$utils.clone(this.addressForm)

        if (addressForm.state) {
          delete addressForm.state
        }
        this.addressForm = addressForm
      }

      this.showStateDropdown = showDropdown
    },
    handleCountryStateDropdownEvent (evtType, value) {
      const elFormItem = $('.el-form-item.state')

      if (evtType === 'changed' && (this.addressForm.state && this.addressForm.state.length > 0)) {
        elFormItem.find('.el-form-item__error').removeClass('transparent')
        elFormItem.find('input').removeClass('transparent-placeholder')
        elFormItem.removeClass('is-error')
      }

      if (evtType === 'focus') {
        elFormItem.find('.el-form-item__error').addClass('transparent')
        elFormItem.find('input').addClass('transparent-placeholder')
        elFormItem.removeClass('is-error')
      }

      if (evtType === 'blur') {
        elFormItem.find('.el-form-item__error').removeClass('transparent')
        elFormItem.find('input').removeClass('transparent-placeholder')
      }
    },
    handleCurrentRequestsFlag (type, requestNameSrc) {
      if (type === 'remove') {
        this.currentRequestsFlag = this.currentRequestsFlag.filter((req) => {
          return req.requestName !== requestNameSrc
        })
      }

      if (type === 'add') {
        this.currentRequestsFlag.push({ requestName: requestNameSrc })
      }
    },
    handleDatatransMessage (event) {
      // Make sure event.origin is from the sandbox to prevent accepting message out of nowhere.
      const datatransOrigin = (new URL(CONFIG.DATATRANS_PAY_API_ROOT_URL)).origin
      if (event.origin !== datatransOrigin) return

      /**
       * Hide loader when datatrans lightbox is ready.
       * When clicking cc-edit-icon, it will createSignature and show loader to prevent multiple clicks that cause multiple requests.
       * Multiple click/requests is the reason why lightbox is not showing.
       */
      if (['frameReady', 'cancel'].includes(event.data)) {
        this.$utils.mainLoader(false)
      }
    },
    addMessageListener () {
      if (!this.handleWindowMessageEventListenerFunction) {
        this.handleWindowMessageEventListenerFunction = this.handleDatatransMessage.bind(this)
        window.addEventListener('message', this.handleWindowMessageEventListenerFunction)
      }
    },
    removeMessageListener () {
      this.svgActivateClicks = { isActivated: false, ref: null }

      if (this.handleWindowMessageEventListenerFunction) {
        window.removeEventListener('message', this.handleWindowMessageEventListenerFunction)
        this.handleWindowMessageEventListenerFunction = null
      }
    },
    debouncedSearch: _.debounce(function (query) {
      this.handleCityDropdownSearch(query)
    }, 300)
  },
  mounted () {
    this.setCountries()
    this.setUSStates()
    this.setUserAddressData()
    this.addMessageListener()

    // Close city address popper
    $(document).on('click', (event) => {
      const targetEl = $(event.target).attr('class')
      if (targetEl && $('.address-city-autosearch-proper').length && !targetEl.includes('el-select-dropdown__item')) {
        this.handleCitySearchPopperVisibility({ style: { display: 'none' } })
      }
    })
  }
}
